import { TFunction } from 'i18next';

import { RowDataSourceTableDto } from '@interfaces-clean/field-rdl-definition.interface';
import {
    ModelPlotBy,
    ModelScaleBy,
    EventRatesOTThresholdType,
    EventRatesOTBenchmarkType,
    EventRatesOTCountryBenchmarkType,
    ModelPlotCalculation,
    ControlHubModelPeriodType,
    AutomationRange,
} from 'types/graphql-global-types';

export type RowDataSource = {
    id: string;
    name: string;
    table: string;
};

export type CalcStartDate = {
    id: string;
    name: string;
};
export type CalcEndDate = {
    id: string | null;
    name?: string | null;
};

export type ScaleTimePoint = {
    id: ModelScaleBy;
    name: string;
};

export type PlotBy = {
    id: ModelPlotBy;
    name: string;
};

export type PlotCalculations = {
    id: ModelPlotCalculation;
    name: string;
};

export type ThresholdType = {
    id: EventRatesOTThresholdType;
    name: string;
};
export type MinTypePeriod = {
    id: ControlHubModelPeriodType;
    name: string;
};
export type BenchmarkType = {
    id: EventRatesOTBenchmarkType;
    name: string;
};
export type CountryType = {
    id: EventRatesOTCountryBenchmarkType;
    name: string;
};

export type RowDataDate = {
    id: string;
    name: string;
};

export type RateBy = {
    id: ModelScaleBy;
    name: string;
};
export type ScaleBy = {
    id: ModelScaleBy;
    name: string;
};

export type Range = {
    id: AutomationRange;
    name: string;
};

export type TimePointsList = {
    id: string;
    name: string;
    table: string;
};

export type TimePointDate = {
    id: string;
    name: string;
};

export type IFormData = {
    // Column 1
    isActive: boolean;
    configId: string;
    plotTitle: string;
    xAxisTitle: string;
    yAxisTitle: string;
    qtl?: { id: string; name: string } | null;
    reportOOT?: { id: string; name: string } | null;

    // Column 2

    rowData: RowDataSource | null;
    rowDataDate: RowDataDate | null;
    rateBy?: string | null;
    scaleBy?: ScaleBy | null;
    scaleTimepoint?: ScaleTimePoint | null;
    timepointsList: TimePointsList | null;
    timepointDate?: TimePointDate | null;
    plotBy?: PlotBy | null;
    plotCalculations?: PlotCalculations | null;
    automationRange?: Range | null;
    numberOfTimePeriods: number | null;
    calcStartDate: CalcStartDate | null;
    calcEndDates: CalcEndDate[] | null;

    // Column 3

    thresholdType: ThresholdType | null;
    upperRed: number | null;
    upperAmber: number | null;
    lowerRed: number | null;
    lowerAmber: number | null;
    xAxisPtsToShade: number | null;
    expectedVolume: number | null;
    minSubjects: number | null;
    minTimeUnit: number | null;
    minTimePeriod: MinTypePeriod | null;

    // Column 4
    studyType: BenchmarkType | null;
    countryValue: CountryType | null;
    studySpecificValue: number | null;
    numeratorName: string | null;
    denominatorName: string | null;
};

export const subjectTables = [
    RowDataSourceTableDto.SUBJECT_VISIT,
    RowDataSourceTableDto.SUBJECT_PROTOCOL_DEVIATION,
    RowDataSourceTableDto.SUBJECT_INFORMED_CONSENT,
    RowDataSourceTableDto.SUBJECT_ASSESSMENT,
    RowDataSourceTableDto.SUBJECT_ADVERSE_EVENT,
    RowDataSourceTableDto.SUBJECT_CRF,
    RowDataSourceTableDto.SUBJECT_ISSUE,
    RowDataSourceTableDto.SUBJECT_LAB,
    RowDataSourceTableDto.SUBJECT_QUERY,
    RowDataSourceTableDto.SUBJECT,
];

export const OptionsEventRatesOT = (t: TFunction) => {
    return {
        ScaleByOptions: [
            { id: ModelScaleBy.PER_SUBJECT_WEEK, name: t(ModelScaleBy.PER_SUBJECT_WEEK) },
            { id: ModelScaleBy.PER_SUBJECT_MONTH, name: t(ModelScaleBy.PER_SUBJECT_MONTH) },
            { id: ModelScaleBy.PER_SUBJECT_YEAR, name: t(ModelScaleBy.PER_SUBJECT_YEAR) },
            { id: ModelScaleBy.PER_10_SUBJECT_YEARS, name: t(ModelScaleBy.PER_10_SUBJECT_YEARS) },
            { id: ModelScaleBy.PER_100_SUBJECT_YEARS, name: t(ModelScaleBy.PER_100_SUBJECT_YEARS) },
            { id: ModelScaleBy.PER_1000_SUBJECT_YEARS, name: t(ModelScaleBy.PER_1000_SUBJECT_YEARS) },
        ],
        ScaleTimePointOptions: [
            { id: ModelScaleBy.PER_SUBJECT_TIME_POINT, name: t(ModelScaleBy.PER_SUBJECT_TIME_POINT) },
            { id: ModelScaleBy.PER_10_SUBJECT_TIME_POINTS, name: t(ModelScaleBy.PER_10_SUBJECT_TIME_POINTS) },
            { id: ModelScaleBy.PER_100_SUBJECT_TIME_POINTS, name: t(ModelScaleBy.PER_100_SUBJECT_TIME_POINTS) },
            { id: ModelScaleBy.PER_1000_SUBJECT_TIME_POINTS, name: t(ModelScaleBy.PER_1000_SUBJECT_TIME_POINTS) },
        ],
        PlotByOptions: [
            { id: ModelPlotBy.MONTHS, name: t(ModelPlotBy.MONTHS) },
            { id: ModelPlotBy.WEEKS, name: t(ModelPlotBy.WEEKS) },
        ],
        PlotCalculationOptions: [
            { id: ModelPlotCalculation.CUMULATIVE, name: t(ModelPlotCalculation.CUMULATIVE) },
            { id: ModelPlotCalculation.BY_TIME_PERIOD, name: t(ModelPlotCalculation.BY_TIME_PERIOD) },
        ],
        ThresholdTypeOptions: [
            { id: EventRatesOTThresholdType.FIXED, name: t(EventRatesOTThresholdType.FIXED) },
            { id: EventRatesOTThresholdType.U_CHART, name: t(EventRatesOTThresholdType.U_CHART) },
            { id: EventRatesOTThresholdType.LANEY_U_CHART, name: t(EventRatesOTThresholdType.LANEY_U_CHART) },
            {
                id: EventRatesOTThresholdType.QUASI_POISSON_U_CHART,
                name: t(EventRatesOTThresholdType.QUASI_POISSON_U_CHART),
            },
        ],
        BenchmarkTypeOptions: [
            { id: EventRatesOTBenchmarkType.NONE, name: t(EventRatesOTBenchmarkType.NONE) },
            { id: EventRatesOTBenchmarkType.STUDY_RATE, name: t(EventRatesOTBenchmarkType.STUDY_RATE) },
            { id: EventRatesOTBenchmarkType.STUDY_SPECIFIC, name: t(EventRatesOTBenchmarkType.STUDY_SPECIFIC) },
        ],
        CountryBenchmarkTypeOptions: [
            {
                id: EventRatesOTCountryBenchmarkType.COUNTRY_RATE,
                name: t(EventRatesOTCountryBenchmarkType.COUNTRY_RATE),
            },
            { id: EventRatesOTCountryBenchmarkType.NONE, name: t(EventRatesOTCountryBenchmarkType.NONE) },
            {
                id: EventRatesOTCountryBenchmarkType.STUDY_SPECIFIC,
                name: t(EventRatesOTCountryBenchmarkType.STUDY_SPECIFIC),
            },
            { id: EventRatesOTCountryBenchmarkType.STUDY_RATE, name: t(EventRatesOTCountryBenchmarkType.STUDY_RATE) },
        ],
        MinTimeOnStudyPeriodOptions: [
            { id: ControlHubModelPeriodType.DAYS, name: t(ControlHubModelPeriodType.DAYS) },
            { id: ControlHubModelPeriodType.WEEKS, name: t(ControlHubModelPeriodType.WEEKS) },
            { id: ControlHubModelPeriodType.MONTHS, name: t(ControlHubModelPeriodType.MONTHS) },
            { id: ControlHubModelPeriodType.QUARTERS, name: t(ControlHubModelPeriodType.QUARTERS) },
            { id: ControlHubModelPeriodType.YEARS, name: t(ControlHubModelPeriodType.YEARS) },
        ],
        YesNoOptions: [
            { id: 'true', name: 'Yes' },
            { id: 'false', name: 'No' },
        ],
        RangeOptions: [
            { id: AutomationRange.ALL, name: t(AutomationRange.ALL) },
            { id: AutomationRange.SPECIFIC_RANGE, name: t(AutomationRange.SPECIFIC_RANGE) },
        ],
    };
};
